import * as React from "react"
import { useCreatePasswordResetMutation } from "@digits-graphql/frontend/graphql-public"
import { sessionError } from "src/frontend/components/Session/Glow/error"
import { useChromeAlerts } from "src/frontend/hooks/useChromeAlerts"
import { useDisableFor } from "src/frontend/hooks/useDisableFor"

export function useCreatePasswordReset(onCompleted: () => void) {
  const { alertError } = useChromeAlerts()

  const onError = React.useCallback(() => {
    TrackJS?.track("Request password reset error")
    alertError(sessionError)
  }, [alertError])

  const [createPasswordResetMutation, { loading }] = useCreatePasswordResetMutation({
    context: { publicAPI: true },
    onError,
    onCompleted,
  })

  const createPasswordReset = React.useCallback(
    (emailAddress: string) =>
      createPasswordResetMutation({
        variables: {
          emailAddress,
        },
      }),
    [createPasswordResetMutation]
  )

  return React.useMemo(
    () => ({
      loading,
      createPasswordReset,
    }),
    [loading, createPasswordReset]
  )
}

export function useCreatePasswordResetWithDelay(email: string) {
  const { alertSuccess } = useChromeAlerts()
  const { disabled, disableForDuration } = useDisableFor(5000)
  const onResetCompleted = React.useCallback(() => {
    alertSuccess("Please check your email!")
  }, [alertSuccess])
  const { loading, createPasswordReset } = useCreatePasswordReset(onResetCompleted)
  const onClick = React.useCallback(() => {
    disableForDuration()
    createPasswordReset(email)
  }, [disableForDuration, createPasswordReset, email])

  return React.useMemo(
    () => ({
      disabled,
      onClick: email ? onClick : undefined,
      loading,
    }),
    [disabled, loading, email, onClick]
  )
}
